import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Button, Card, Callout, Field, TextInput, ToastProvider, useToast } from '@api-hub/ui';
import { api, describeError } from './lib/api';
import { useResource } from './lib/hooks';
import { t } from './lib/i18n';
import { Layout } from './components/Layout';
import { DashboardPage } from './pages/Dashboard';
import { RequestsPage } from './pages/Requests';
import { UsagePage } from './pages/Usage';
import { AuditPage } from './pages/Audit';
import { ProvidersPage } from './pages/Providers';
import { ConnectionsPage } from './pages/Connections';
import { UpstreamMcpPage } from './pages/UpstreamMcp';
import { ServicesPage } from './pages/Services';
import { OperationsPage } from './pages/Operations';
import { ModelAliasesPage } from './pages/ModelAliases';
import { OpenApiImportPage } from './pages/OpenApiImport';
import { KnowledgePage } from './pages/Knowledge';
import { CatalogVersionsPage } from './pages/CatalogVersions';
import { ClientAppsPage } from './pages/ClientApps';
import { HubKeysPage } from './pages/HubKeys';
import { DeveloperTokensPage } from './pages/DeveloperTokens';
import { ToolsetsPage } from './pages/Toolsets';
import { McpPreviewPage } from './pages/McpPreview';
import { IntegrationPacksPage } from './pages/IntegrationPacks';
import { TestConsolePage } from './pages/TestConsole';
import { UsersPage } from './pages/Users';
import { SettingsPage } from './pages/Settings';
import { ChangePasswordPage } from './pages/ChangePassword';
import { ApiReferencePage } from './pages/ApiReference';

export interface SessionUser {
  id: string;
  email: string;
  username: string | null;
  displayName: string;
  role: 'OWNER' | 'ADMIN' | 'OPERATOR' | 'VIEWER';
  mustChangePassword: boolean;
}

interface SignInResult extends SessionUser {
  twoFactorRequired?: boolean;
  challengeId?: string;
}

function LoginScreen({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only when the password was accepted and a code is outstanding.
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (challengeId) {
        const user = await api.post<SessionUser>('/api/admin/auth/2fa', { challengeId, code });
        onSignedIn(user);
        return;
      }
      const result = await api.post<SignInResult>('/api/admin/auth/login', { login, password });
      if (result.twoFactorRequired && result.challengeId) {
        setChallengeId(result.challengeId);
        setCode('');
        return;
      }
      onSignedIn(result);
    } catch (cause) {
      setError(describeError(cause).title);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ah-login">
      <Card title={t('app.title')} description={t('app.subtitle')}>
        <form onSubmit={submit}>
          {challengeId ? (
            <Field label={t('auth.code')}>
              {(id) => (
                <TextInput
                  id={id}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              )}
            </Field>
          ) : (
            <>
              <Field label={t('auth.login')}>
                {(id) => (
                  <TextInput
                    id={id}
                    type="text"
                    autoComplete="username"
                    required
                    value={login}
                    onChange={(event) => setLogin(event.target.value)}
                  />
                )}
              </Field>
              <Field label={t('auth.password')}>
                {(id) => (
                  <TextInput
                    id={id}
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                )}
              </Field>
            </>
          )}
          {error && <Callout tone="danger">{error}</Callout>}
          <Button type="submit" variant="primary" disabled={busy} style={{ width: '100%' }}>
            {busy ? t('common.loading') : challengeId ? t('auth.confirmCode') : t('auth.signIn')}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function Shell() {
  // The session is just another resource; `override` lets sign-in and sign-out
  // update the view immediately without an extra round trip.
  const session = useResource<SessionUser>('/api/admin/auth/me');
  const [override, setOverride] = useState<SessionUser | null | undefined>(undefined);
  const toast = useToast();

  const user = override !== undefined ? override : session.data;
  const mustChangePassword = user?.mustChangePassword ?? false;

  useEffect(() => {
    // Notifying the toast system is an external side effect, not state sync.
    if (mustChangePassword) toast.push({ title: t('auth.mustChangePassword'), tone: 'info' });
  }, [mustChangePassword, toast]);

  if (override === undefined && session.loading) {
    return <div className="ah-login">{t('common.loading')}</div>;
  }
  if (!user) {
    return <LoginScreen onSignedIn={setOverride} />;
  }

  return (
    <Routes>
      <Route element={<Layout user={user} onSignedOut={() => setOverride(null)} />}>
        <Route index element={<DashboardPage />} />
        <Route path="requests" element={<RequestsPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="providers" element={<ProvidersPage role={user.role} />} />
        <Route path="connections" element={<ConnectionsPage role={user.role} />} />
        <Route path="upstream-mcp" element={<UpstreamMcpPage role={user.role} />} />
        <Route path="services" element={<ServicesPage role={user.role} />} />
        <Route path="operations" element={<OperationsPage role={user.role} />} />
        <Route path="model-aliases" element={<ModelAliasesPage role={user.role} />} />
        <Route path="openapi-import" element={<OpenApiImportPage />} />
        <Route path="knowledge" element={<KnowledgePage role={user.role} />} />
        <Route path="catalog-versions" element={<CatalogVersionsPage role={user.role} />} />
        <Route path="client-apps" element={<ClientAppsPage role={user.role} />} />
        <Route path="hub-keys" element={<HubKeysPage role={user.role} />} />
        <Route path="developer-tokens" element={<DeveloperTokensPage user={user} />} />
        <Route path="toolsets" element={<ToolsetsPage role={user.role} />} />
        <Route path="mcp-preview" element={<McpPreviewPage />} />
        <Route path="integration-packs" element={<IntegrationPacksPage role={user.role} />} />
        <Route path="test-console" element={<TestConsolePage role={user.role} />} />
        <Route path="users" element={<UsersPage role={user.role} />} />
        <Route path="settings" element={<SettingsPage role={user.role} />} />
        <Route path="api-reference" element={<ApiReferencePage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ToastProvider>
  );
}
